import { useMemo, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Modal,
  Button,
  TextInput,
  Select,
  Group,
  Title,
  Tabs,
  Text,
  useMantineTheme,
  Switch,
  Image,
  Stack,
  ScrollArea
} from '@mantine/core';
import { MantineReactTable, type MRT_ColumnDef, type MRT_Row } from 'mantine-react-table';
import { useDropzone } from 'react-dropzone';
import { IconCloudUpload, IconFileExcel } from '@tabler/icons-react';
import { MRT_DensityState } from 'mantine-react-table';
import Environment from '../utility/environment';
import { useAuth } from '../contexts/AuthProvider';
import * as XLSX from 'xlsx';
import { useRef } from "react";
import { parsePhotoUrl } from '../utility/parsers';
import { useMrtTableContainerProps, useModalScrollAreaHeight, usePageTableShellStyle } from '../utility/mobilePageLayout';
import { PhoneInput, PhoneInputValue } from '../components/PhoneInput';
import { buildMrtLocalization } from '../i18n/mrtLocale';
import { enableGlobalSearchOnColumns, withMrtContainsSearch } from '../utility/mrtTableSearch';

type FileUploadByImageProps = {
  user: UserForm;
  setUserForm: React.Dispatch<React.SetStateAction<UserForm>>;
};

export const FileUploadByImage: React.FC<FileUploadByImageProps> = ({ user, setUserForm }) => {
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const handleImageClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];

    if (file) {
      console.log("Arquivo selecionado:", file.name);
      setUserForm((prevUserForm) => ({ ...prevUserForm, photo: file }));
      // Aqui você pode fazer upload, preview, etc.
    }
  };

  const parsePhoto = (photo: string | File) => {
    if (typeof photo === 'string') {
      return parsePhotoUrl(photo);
    }
    return URL.createObjectURL(photo);
  };

  return (
    <div>
      {/* Imagem clicável */}
      <Image
        src={parsePhoto(user.photo)}// Coloque a URL ou import da sua imagem
        alt="Upload"
        onClick={handleImageClick}
        width={200}
        height={200}
        style={{
          cursor: "pointer",
          objectFit: "cover",
          borderRadius: "50%" // opcional, caso queira redonda 
        }}
      />

      {/* Input de arquivo invisível */}
      <input
        type="file"
        ref={fileInputRef}
        style={{ display: "none" }}
        onChange={handleFileChange}
        accept="image/*" // Ou qualquer outro tipo, como application/pdf
      />
    </div>
  );
};


type User = {
  id: number;
  uuid: string;
  enterprise_id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  role: string;
  cargo: string;
  username: string;
  photo: string | File;
  status: string;
};

type UserForm = Omit<User, 'id' | 'uuid' | 'enterprise_id'>;

const initialForm: UserForm = {
  first_name: '',
  last_name: '',
  email: '',
  role: 'user',
  cargo: '',
  username: '',
  photo: 'https://cdn-icons-png.flaticon.com/512/149/149452.png',
  phone: '',
  status: 'active',
};


export default function UsersPage() {
  const { t, i18n } = useTranslation();
  const { accessToken, actualEnterprise } = useAuth();
  const tableShellStyle = usePageTableShellStyle();
  const mrtTableContainerProps = useMrtTableContainerProps();
  const modalScrollAreaHeight = useModalScrollAreaHeight();

  const [users, setUsers] = useState<User[]>([]);

  const [modalOpened, setModalOpened] = useState(false);
  const [modalMode, setModalMode] = useState<'manual' | 'automático'>('manual');
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [isEditingCampaign, setIsEditingCampaign] = useState(false);
  const [userForm, setUserForm] = useState<UserForm>(initialForm);
  const [errorMessage, setErrorMessage] = useState('');

  // Estado para armazenar o arquivo importado via dropzone
  const [autoImportFile, setAutoImportFile] = useState<File | null>(null);
  const [importErrors, setImportErrors] = useState<string[]>([]);
  const [notification, setNotification] = useState<{ message: string; color: 'green' | 'red' } | null>(null);
  const theme = useMantineTheme();
  const [usernameError, setUsernameError] = useState<string | undefined>(undefined);
  const [emailError, setEmailError] = useState<string | undefined>(undefined);

  const nameLength = 50;

  const emailRegex = /^[a-zA-Z0-9_.+-]+@[a-zA-Z0-9-]+\.[a-zA-Z0-9-.]+$/;
  const usernameRegex = /^[a-zA-Z0-9_.-]+$/;
  const phoneRegex = /^\+\d{1,3}\s? \d{1,14}(?:\s\d{1,13})?$/;


  useEffect(() => {
    getUsers();
  }, [accessToken, actualEnterprise]);

  useEffect(() => {
    document.title = 'aPlus CRM - Campaign';
  }, []);

  const isFormValid = () => {
    return (
      userForm.first_name.trim() !== '' &&
      userForm.last_name.trim() !== '' &&
      userForm.email.trim() !== '' &&
      userForm.role.trim() !== '' &&
      userForm.cargo.trim() !== '' &&
      userForm.username.trim() !== '' &&
      userForm.username.length >= 6 &&
      emailRegex.test(userForm.email) &&
      usernameRegex.test(userForm.username)
    );
  };

  const openModal = (user?: User) => {
    if (user) {
      setEditingUser(user);
      setUserForm({
        first_name: user.first_name,
        last_name: user.last_name,
        email: user.email,
        role: user.role,
        cargo: user.cargo,
        username: user.username,
        photo: user.photo,
        phone: user.phone,
        status: user.status,
      });
    } else {
      setEditingUser(null);
      setUserForm(initialForm);
    }
    setIsEditingCampaign(false);
    setModalMode('manual');
    setModalOpened(true);
    setAutoImportFile(null);
    setErrorMessage('');
    setNotification(null);
    setImportErrors([]);
    setUsernameError(undefined);
    setEmailError(undefined);
  };

  const sendPhoto = async (): Promise<string | null> => {
    const file = userForm.photo;

    if (file instanceof File) {
      const formData = new FormData();
      formData.append('file', file);

      try {
        const response = await fetch(`${Environment.VITE_API_URL}/api/v1/upload-image`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
          credentials: 'include',
          body: formData,
        });

        if (response.ok) {
          const data = await response.json();
          return data.image_url; // Retorna apenas a URL da imagem
        }
      } catch (error) {
        console.error('Erro ao enviar imagem', error);
      }
    }

    return null;
  };


  const getUsers = () => {
    fetch(`${Environment.VITE_API_URL}/api/v1/users/${actualEnterprise?.id}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      credentials: 'include',
    })
      .then((res) => res.json())
      .then((data) => {
        const formattedUsers: User[] = data.map((user: any, index: number) => ({
          id: index + 1,
          uuid: user.id,
          first_name: user.first_name,
          last_name: user.last_name,
          email: user.email,
          role: user.role,
          cargo: user.cargo,
          username: user.username,
          photo: user.photo,
          phone: user.phone_number,
          status: user.status,
        })
        );
        console.log('Usuarios:', data);
        setUsers(formattedUsers);
      })
      .catch((err) => console.error('Erro ao buscar clientes:', err));
  };

  const deleteUser = () => {
    fetch(`${Environment.VITE_API_URL}/api/v1/users/${editingUser?.uuid}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      credentials: 'include',
    })
      .then(async (response) => {
        if (!response.ok) {
          setNotification({ message: 'O usuario nao pode ser deletada, pois está vinculado a alguma oportunidade ou atividade', color: 'red' });
          setErrorMessage('O usuario nao pode ser deletada, pois está vinculado a alguma oportunidade ou atividade');
        }
        console.log(response);
        if (response.status == 200) {
          setModalOpened(false);
          getUsers();
        }
      })
      .catch(() => {
        // console.error(error);
        setModalOpened(true);
      });
  };

  const updateUser = async () => {
    const uploadedPhotoUrl = await sendPhoto();
    var userPayload

    if (uploadedPhotoUrl) {
      userPayload = {
        ...userForm,
        photo: uploadedPhotoUrl, // Aqui garantimos que é string
      }
    } else {
      userPayload = {
        ...userForm,
      }
    }

    fetch(`${Environment.VITE_API_URL}/api/v1/users/${editingUser?.uuid}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      credentials: 'include',
      body: JSON.stringify(formatUserForCreation(userPayload)),
    })
      .then((res) => res.json())
      .then((data) => {
        console.log(data);
        setModalOpened(false);
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {

        getUsers();
      });
  };

  const createUser = async () => {
    const uploadedPhotoUrl = await sendPhoto();
    var userPayload

    if (uploadedPhotoUrl) {
      userPayload = {
        ...userForm,
        photo: uploadedPhotoUrl, // Aqui garantimos que é string
      }
    } else {
      userPayload = {
        ...userForm,
      }

      fetch(`${Environment.VITE_API_URL}/api/v1/users/${actualEnterprise?.id}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        credentials: 'include',
        body: JSON.stringify(formatUserForCreation(userPayload)),
      })
        .then((res) => res.json())
        .then((data) => {
          console.log(data);
          setModalOpened(false);
        })
        .catch((error) => {
          console.error(error);
          setNotification({ message: 'Erro ao criar o usuário', color: 'red' });
          setErrorMessage('Erro ao criar o usuário: Email ou Username em uso');
        })
        .finally(() => {
          getUsers();
        });
    }
  };

  const validateUsername = async (username: string) => {
    if (!username || username.length < 6 || username.length > 20 || !usernameRegex.test(username)) {
      return; // Falha local, não precisa ir ao backend
    }

    try {
      const res = await fetch(`${Environment.VITE_API_URL}/api/v1/validate_username/${username}`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
        credentials: 'include',
      });

      if (!res.ok) {
        const data = await res.json();
        if (data?.detail === "Username already exists") {
          setUsernameError("Username já existe");
          return false
        } else {
          setUsernameError("Erro ao validar username");
          return false
        }
      } else {
        setUsernameError(undefined); // Username válido
        return true
      }
    } catch (error) {
      console.error(error);
      setUsernameError("Erro de conexão com servidor");
      return false
    }
  };

  const validateEmail = async (email: string) => {
    if (!emailRegex.test(email)) {
      return; // Falha local, não precisa ir ao backend
    }

    try {
      const res = await fetch(`${Environment.VITE_API_URL}/api/v1/validate_email/${email}`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
        credentials: 'include',
      });

      if (!res.ok) {
        const data = await res.json();
        if (data?.detail === "Email already exists") {
          setEmailError("Email já existe");
          return false
        } else {
          setEmailError("Erro ao validar o email");
          return false
        }
      } else {
        setEmailError(undefined); // Username válido
        return true
      }
    } catch (error) {
      console.error(error);
      setEmailError("Erro de conexão com servidor");
      return false
    }
  };





  const formatUserForCreation = (contact: UserForm) => {
    return {
      first_name: contact.first_name,
      last_name: contact.last_name,
      email: contact.email,
      role: contact.role,
      cargo: contact.cargo,
      username: contact.username,
      photo: contact.photo,
      status: contact.status,
      phone_number: contact.phone
    };
  };

  const generateExcelTemplate = () => {
    // Definir as chaves do objeto initialClientForm como cabeçalhos
    const worksheetData = [
      [
        'Nome',
        'Sobrenome',
        'Email',
        'Telefone',
        'Acesso',
        'Cargo',
        'Username',
        'Status'
      ], // Cabeçalhos
      [
        'Primeiro Nome',
        'Sobrenome',
        'email@dominio.com',
        '+5511999998888',
        'user',
        'Cirurgião',
        'username',
        'active',
      ],
      [
        'Primeiro Nome',
        'Sobrenome',
        'email@dominio.com',
        '+5511999998888',
        'admin',
        'Cirurgião',
        'username',
        'inactive',
      ],
    ];

    // Criar a planilha com base nos dados
    const ws = XLSX.utils.aoa_to_sheet(worksheetData);

    // Exemplo de dataValidations (se não for usar, pode remover)
    ws['dataValidations'] = [
      {
        sqref: 'I2:I1048576', // Intervalo para a coluna "Hospital/Clínica"
        type: 'list',
        formula1: '"Hospital,Clínica"', // Lista de opções permitidas
        showErrorMessage: true,
        errorTitle: 'Erro de Validação',
        error: "Por favor, insira 'Hospital' ou 'Clínica'.",
      },
      {
        sqref: 'J2:J1048576', // Intervalo para a coluna "Público/Privado"
        type: 'list',
        formula1: '"Público,Privado"',
        showErrorMessage: true,
        errorTitle: 'Erro de Validação',
        error: "Por favor, insira 'Público' ou 'Privado'.",
      },
      {
        sqref: 'K2:K1048576', // Intervalo para a coluna "Status"
        type: 'list',
        formula1: '"TRUE,FALSE"',
        showErrorMessage: true,
        errorTitle: 'Erro de Validação',
        error: "Por favor, insira 'TRUE' ou 'FALSE' para Status.",
      },
    ];

    // Criar a nova pasta de trabalho (workbook)
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Template');

    // Gerar o arquivo Excel em formato binário
    const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });

    // Criar um link de download para o usuário
    const blob = new Blob([wbout], { type: 'application/octet-stream' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'template_campaign_form.xlsx'; // Nome do arquivo
    link.click();
  };

  const handleFileImport = (files: File[]) => {
    if (files.length) {
      setAutoImportFile(files[0]);
      console.log('Arquivo importado:', files[0]);
    }
  };

  // Função para enviar o arquivo importado
  const handleAutoImport = async () => {
    if (autoImportFile) {
      try {
        const data = await autoImportFile.arrayBuffer();
        const workbook = XLSX.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const sheet = workbook.Sheets[sheetName];
        const rawData = XLSX.utils.sheet_to_json(sheet);

        const errors: string[] = [];
        const validContacts: UserForm[] = [];

        rawData.forEach(async (row: any, index: number) => {
          const contact: UserForm = {
            first_name: row['Nome'] || '',
            last_name: row['Sobrenome'] || '',
            email: row['Email'] || '',
            role: row['Acesso'] || '',
            cargo: row['Cargo'] || '',
            username: row['Username'] || '',
            photo: initialForm.photo,
            phone: row['Telefone'] || '',
            status: row['Status'] || '',
          };

          const rowErrors: string[] = [];
          // Validação de campos
          if (!contact.first_name) rowErrors.push('O Nome é obrigatório');
          if (!contact.last_name) rowErrors.push('O Sobrenome é obrigatório');
          if (!contact.email) rowErrors.push('O Email é obrigatório');
          if (!emailRegex.test(contact.email)) rowErrors.push('Email inválido');
          if (!contact.phone) rowErrors.push('O Telefone é obrigatório');
          if (!phoneRegex.test(contact.phone)) rowErrors.push('Telefone inválido');
          if (!contact.role) rowErrors.push('O Acesso é obrigatório');
          if (!contact.cargo) rowErrors.push('O Cargo é obrigatório');
          if (!contact.username) rowErrors.push('O Username é obrigatório');
          if (!contact.status) rowErrors.push('O Status é obrigatório');
          if (await validateEmail(contact.email)) rowErrors.push('Email já cadastrado');
          if (await validateUsername(contact.username)) rowErrors.push('Username já cadastrado');

          if (rowErrors.length > 0) {
            errors.push(`Erro na linha ${index + 2}: ${rowErrors.join(' ')}`);
          } else {
            validContacts.push(contact);
          }
        });

        if (errors.length > 0) {
          setNotification({ message: 'Erro ao importar alguns usuarios', color: 'red' });
          setErrorMessage('Erro ao importar alguns usuarios');
          setImportErrors(errors);
        } else {
          // Mapeando para o formato Contact antes de enviar
          const formattedContacts = validContacts.map(formatUserForCreation);

          // Enviando para a API
          const response = await fetch(`${Environment.VITE_API_URL}/api/v1/users_bulk/${actualEnterprise?.id}`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${accessToken}`,
            },
            credentials: 'include',
            body: JSON.stringify(formattedContacts),
          });

          if (response.ok) {
            setNotification({ message: 'Clientes importados com sucesso', color: 'green' });
            setModalOpened(false);
            getUsers();
          } else {
            console.log(response)
            setNotification({ message: 'Erro ao importar clientes', color: 'red' });
          }
        }
      } catch (error) {
        console.error('Erro ao processar arquivo:', error);
        setNotification({ message: 'Erro ao processar arquivo', color: 'red' });
      } finally {
        // final do try/catch
      }
    } else {
      console.log('Nenhum arquivo selecionado');
    }
  };

  // Configuração do react-dropzone
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: handleFileImport,
    accept: {
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls'],
    },
    maxSize: 30 * 1024 * 1024,
  });

  // Estilo inline para a área do dropzone
  const dropzoneStyle = {
    padding: '1rem',
    border: '1px dashed #ccc',
    textAlign: 'center' as const,
    borderRadius: '8px',
  };

  const localization = useMemo(() => buildMrtLocalization(t), [t, i18n.language]);

  const columns = useMemo<MRT_ColumnDef<User>[]>(
    () => enableGlobalSearchOnColumns(
      [
      {
        accessorKey: 'first_name',
        header: t('users.columns.firstName'),
        mantineTableHeadCellProps: { sx: { backgroundColor: 'rgba(48,113,91,0.2)', color: '#fff' } },
        mantineTableBodyCellProps: { sx: { textAlign: 'left' } },
      },
      {
        accessorKey: 'last_name',
        header: t('users.columns.surname'),
        mantineTableHeadCellProps: { sx: { backgroundColor: 'rgba(48,113,91,0.2)', color: '#fff' } },
        mantineTableBodyCellProps: { sx: { textAlign: 'left' } },
      },
      {
        accessorKey: 'username',
        header: t('users.columns.username'),
        mantineTableHeadCellProps: { sx: { backgroundColor: 'rgba(48,113,91,0.2)', color: '#fff' } },
        mantineTableBodyCellProps: { sx: { textAlign: 'left' } },
      },
      {
        accessorKey: 'email',
        header: t('users.columns.email'),
        mantineTableHeadCellProps: { sx: { backgroundColor: 'rgba(48,113,91,0.2)', color: '#fff' } },
        mantineTableBodyCellProps: { sx: { textAlign: 'left' } },
      },
      {
        accessorKey: 'role',
        header: t('users.columns.access'),
        mantineTableHeadCellProps: { sx: { backgroundColor: 'rgba(48,113,91,0.2)', color: '#fff' } },
        mantineTableBodyCellProps: { sx: { textAlign: 'center' } },
        Cell: ({ cell }) => {
          const role = cell.getValue<string>()
          if (!role) return '';
          if (role === 'admin') return t('users.form.accessAdmin');
          if (role === 'user') return t('users.form.accessUser');
          return role;
        },
      },
      {
        accessorKey: 'cargo',
        header: t('users.columns.role'),
        mantineTableHeadCellProps: { sx: { backgroundColor: 'rgba(48,113,91,0.2)', color: '#fff' } },
        mantineTableBodyCellProps: { sx: { textAlign: 'center' } },
      },
      {
        accessorKey: 'status',
        header: t('users.columns.status'),
        mantineTableHeadCellProps: { sx: { backgroundColor: 'rgba(48,113,91,0.2)', color: '#fff' } },
        mantineTableBodyCellProps: { sx: { textAlign: 'center' } },
        Cell: ({ cell }) => {
          const status = cell.getValue<string>()
          if (!status) return '';
          if (status === 'active') return t('users.form.statusActive');
          if (status === 'inactive') return t('users.form.statusInactive');
          return status;
        },
      }
    ],
      ['first_name', 'last_name', 'username', 'email'],
    ),
    [t, i18n.language]
  );



  const handleRowDoubleClick = (rowData: User) => {
    setModalMode('manual');
    setModalOpened(true);
    setEditingUser(rowData);
    setUserForm(rowData);
    setIsEditingCampaign(true);
    setErrorMessage('');
    setUsernameError(undefined);
    setEmailError(undefined);
    console.log('Dados da linha clicada:', rowData);
  };

  return (
    <>
      {/* Cabeçalho com botão */}
      <Group position="apart" align="center" mb="md">
        <Title order={1}>{t('users.title')}</Title>
        <Group>
          <Button onClick={() => openModal()}>{t('users.addUser')}</Button>
        </Group>
      </Group>

      <div style={tableShellStyle}>
        <MantineReactTable
          {...withMrtContainsSearch({
          columns,
          data: users,
          localization,
          defaultColumn: { size: 40, minSize: 30, maxSize: 600 },
          enableStickyHeader: true,
        enableColumnFilters: true,
          enableSorting: true,
          enableFullScreenToggle: true,
          enableGrouping: false,
          enableColumnActions: false,
          enablePagination: true,
          enableBottomToolbar: true,
          enableRowVirtualization: false,
          initialState: {
            density: 'sm' as MRT_DensityState,
            showGlobalFilter: true,
            pagination: {
              pageSize: 20, // ✅ Set to 20 rows per page
              pageIndex: 0, // ✅ Start from the first page
            },
          },
          mantineTableContainerProps: mrtTableContainerProps,
          mantineTableProps: {
            sx: {
              display: 'table',
              tableLayout: 'auto',
              borderCollapse: 'separate',
              '& .mantine-TableHeadCell-Content': {
                justifyContent: 'center',
                alignItems: 'center',
                padding: '1px 1px', // Ajuste fino do padding se necessário
              },
              '& .mantine-TextInput-input': {
                backgroundColor: 'rgba(255, 255, 255, 0.6)',
                borderRadius: '4px',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                color: '#000', // Garantir contraste
              },
              '& th, & td': {
                borderRadius: '4px',
              },
              '& thead': {
                position: 'sticky',
                top: 0,
                zIndex: 100, // Cabeçalho sempre visível
                backgroundColor: 'rgba(48, 113, 91, 0.9)',
                color: '#fff',
              },
            },
          },
          mantineTableBodyRowProps: ({ row }: { row: MRT_Row<User> }) => ({
            onDoubleClick: () => handleRowDoubleClick(row.original),
            style: { cursor: 'pointer' }, // Indica que a linha é clicável
          }),
          })}
        />
      </div>

      {/* Modal para adicionar/editar ou importar clientes */}
      <Modal
        opened={modalOpened}
        onClose={() => setModalOpened(false)}
        centered
        title={
          modalMode === 'manual'
            ? editingUser
              ? t('users.updateUser')
              : t('users.addNewUser')
            : t('users.importUsers')
        }
        size="xl"
        styles={{ body: { padding: '0' } }}
      >
        <Tabs
          value={modalMode}
          onTabChange={(value: 'manual' | 'automático') => setModalMode(value)}
        >
          {/* Quando estiver editando um cliente, não mostra as abas */}
          {!isEditingCampaign && (
            <Tabs.List>
              <Tabs.Tab value="manual">{t('common.import.manual')}</Tabs.Tab>
              <Tabs.Tab value="automático">{t('common.import.automatic')}</Tabs.Tab>
            </Tabs.List>
          )}

          {/* Aba Manual */}
          <Tabs.Panel value="manual" pt="xs">
            <ScrollArea w="100%" h={modalScrollAreaHeight}>
              <div style={{ padding: '1rem' }}>
                <Group
                  spacing="md"
                  mb="sm"
                  align="flex-end"
                  style={{ maxWidth: '100%' }}>
                  {/* <Image
                src={userForm.photo || 'https://cdn-icons-png.flaticon.com/512/149/149452.png'} // URL da imagem padrão
                width={150}
                height={150}
                radius="md"
                fit="cover"
                withPlaceholder
                alt="Imagem do Usuário"
                style={{ flex: 1 }}
                onClick={() => fileInputRef.current?.click()}
                /> */}
                  <FileUploadByImage user={userForm} setUserForm={setUserForm} />
                  <Group mb="sm" style={{ flexDirection: 'column', flex: 3 }}>
                    <TextInput
                      label={t('users.form.firstName')}
                      placeholder={t('users.form.firstNamePlaceholder')}
                      value={userForm.first_name}
                      onChange={(e) =>
                        setUserForm({ ...userForm, first_name: e.target.value })
                      }
                      mb="sm"
                      required
                      style={{ flex: 1, width: '80%' }}

                    />
                    <Text className={`text-input-counter ${userForm.first_name.length > nameLength ? "error" : ""}`} style={{ width: '80%' }}>
                      {t('settings.characters', { count: userForm.first_name.length, max: nameLength })}
                    </Text>
                    <TextInput
                      label={t('users.form.surname')}
                      placeholder={t('users.form.surnamePlaceholder')}
                      value={userForm.last_name}
                      onChange={(e) =>
                        setUserForm({ ...userForm, last_name: e.target.value })
                      }
                      mb="sm"
                      required
                      style={{ flex: 1, width: '80%' }}
                    />
                    <Text className={`text-input-counter ${userForm.last_name.length > nameLength ? "error" : ""}`} style={{ width: '80%' }}>
                      {t('settings.characters', { count: userForm.last_name.length, max: nameLength })}
                    </Text>
                  </Group>
                </Group>

                <Group grow mb="sm">
                  <Stack>
                    <TextInput
                      label={t('users.form.username')}
                      placeholder={t('users.form.usernamePlaceholder')}
                      value={userForm.username}
                      onChange={(e) => {
                        const value = e.target.value;
                        setUserForm({ ...userForm, username: value });

                        // Limpa o erro enquanto digita
                        setUsernameError(undefined);
                      }}
                      onBlur={() => validateUsername(userForm.username)}
                      mb="sm"
                      required
                      disabled={isEditingCampaign}
                      error={
                        (userForm.username && userForm.username.length < 6
                          ? t('users.validation.usernameMinLength')
                          : undefined) ||
                        (userForm.username && !usernameRegex.test(userForm.username)
                          ? t('users.validation.usernameInvalidFormat')
                          : undefined) ||
                        (userForm.username && userForm.username.length > 20
                          ? t('users.validation.usernameMaxLength')
                          : undefined) ||
                        usernameError
                      }
                      maxLength={nameLength}
                    />
                    <Text className={`text-input-counter ${userForm.username.length > nameLength ? "error" : ""}`}>
                      {t('settings.characters', { count: userForm.username.length, max: nameLength })}
                    </Text>
                  </Stack>
                </Group>


                <Group grow mb="sm">
                  <Stack>
                    <TextInput
                      label={t('users.form.email')}
                      placeholder={t('users.form.emailPlaceholder')}
                      value={userForm.email}
                      onChange={(e) =>
                        setUserForm({ ...userForm, email: e.target.value })
                      }
                      mb="sm"
                      required
                      error={
                        (userForm.email && !emailRegex.test(userForm.email) ? t('settings.invalidEmailFormat') : undefined) ||
                        emailError
                      }
                      onBlur={() => {
                        if (isEditingCampaign) {
                          var user = users.find((user) => user.username === userForm.username);
                          if (user && user.email !== userForm.email) {
                            validateEmail(userForm.email)
                          }
                        } else {
                          validateEmail(userForm.email)
                        }
                      }
                      }
                      maxLength={nameLength}
                    />
                    <Text className={`text-input-counter ${userForm.email.length > nameLength ? "error" : ""}`}>
                      {t('settings.characters', { count: userForm.email.length, max: nameLength })}
                    </Text>
                  </Stack>

                  <Stack>
                    <PhoneInput
                      value={{
                        country: userForm.phone.split(' ')[0] || '+55',
                        phone: userForm.phone.split(' ').slice(1).join(' ') || '',
                      }}
                      onChange={(value: PhoneInputValue) =>
                        setUserForm({
                          ...userForm,
                          phone: `${value.country} ${value.phone}`.trim(),
                        })
                      }
                    />
                    <Text
                      className={`text-input-counter ${userForm.phone.length > nameLength ? 'error' : ''
                        }`}
                      style={{ marginTop: -13 /* ajuste em px conforme necessário */ }}
                    >
                      {t('settings.characters', { count: userForm.phone.length, max: nameLength })}
                    </Text>
                  </Stack>
                </Group>

                <Group grow mb="sm">
                  <Select
                    label={t('users.columns.access')}
                    placeholder={t('common.select')}
                    value={userForm.role}
                    onChange={(value) =>
                      setUserForm({
                        ...userForm,
                        role: value as
                          | 'user'
                          | 'admin'
                      })
                    }
                    data={[
                      { value: 'user', label: t('users.form.accessUser') },
                      { value: 'admin', label: t('users.form.accessAdmin') },
                    ]}
                    mb="sm"
                    required
                    style={{ paddingBottom: 10 }}
                  />
                  <Stack>
                    <TextInput
                      label={t('users.columns.role')}
                      placeholder={t('users.form.rolePlaceholder')}
                      value={userForm.cargo}
                      onChange={(e) =>
                        setUserForm({ ...userForm, cargo: e.target.value })
                      }
                      mb="sm"
                      required
                    />
                    <Text className={`text-input-counter ${userForm.cargo.length > nameLength ? "error" : ""}`}>
                      {t('settings.characters', { count: userForm.cargo.length, max: nameLength })}
                    </Text>
                  </Stack>
                </Group>
                <Switch
                  label={t('users.columns.status')}
                  checked={userForm.status === 'active'}
                  onChange={(e) =>
                    setUserForm({ ...userForm, status: e.currentTarget.checked ? 'active' : 'inactive' })
                  }
                  mb="sm"
                />
              </div>
            </ScrollArea>
            <Group position="right" mt="md" style={{ padding: '1rem' }}>
              {isEditingCampaign && (
                <Button
                  onClick={deleteUser}
                  sx={{
                    backgroundColor: 'rgba(163, 0, 0, 0.9)',
                    color: 'white',
                    '&:hover': { backgroundColor: 'rgba(121, 0, 0, 0.9)' },
                  }}
                >
                  {t('common.delete')}
                </Button>
              )}
              <Button
                onClick={isEditingCampaign ? updateUser : createUser}
                disabled={!isFormValid()}
              >
                {isEditingCampaign ? t('common.update') : t('common.save')}
              </Button>
            </Group>

            {errorMessage && (
              <Group position="center" mt="md" color="red">
                <Text className="error-message" color="red">
                  {errorMessage !== '' ? errorMessage : null}
                </Text>
              </Group>
            )}
          </Tabs.Panel>

          {/* Aba Automático */}
          <Tabs.Panel value="automático" pt="xs">
            <div {...getRootProps()} style={dropzoneStyle}>
              <input {...getInputProps()} />
              {autoImportFile ? (
                <Group style={{ flexDirection: 'column' }} spacing="xs">
                  <IconFileExcel size={50} color={theme.colors.green[6]} stroke={1.5} />
                  <Text align="center" weight={700} size="lg">
                    {autoImportFile.name}
                  </Text>
                </Group>
              ) : (
                <>
                  {isDragActive ? (
                    <IconCloudUpload size={50} color={theme.colors.blue[6]} stroke={1.5} />
                  ) : (
                    <IconCloudUpload size={50} stroke={1.5} />
                  )}
                  <Text align="center" weight={700} size="lg" mt="xl">
                    Drag and drop or click to upload
                  </Text>
                  <Text align="center" size="sm" mt="xs" color="dimmed">
                    Only Excel files (.xlsx, .xls) up to 30MB are allowed.
                  </Text>
                </>
              )}
            </div>
            {/* Exibição de erros ou sucesso */}
            <div style={{ marginTop: '16px' }}>
              {importErrors.length > 0 && (
                <Group spacing="xs">
                  {importErrors.map((error, index) => (
                    <Text key={index} color="red">
                      {error}
                    </Text>
                  ))}
                </Group>
              )}
              {
                <Text color={notification?.color} align="center">
                  {notification?.message}
                </Text>
              }
            </div>
            <Group mt="md" position="apart" align="center">
              <Group spacing="xs" style={{ cursor: 'pointer' }} onClick={generateExcelTemplate}>
                <IconFileExcel size={24} stroke={1.5} color={theme.colors.green[6]} />
                <Text
                  size="sm"
                  weight={700}
                  style={{ textDecoration: 'underline' }}
                  color={theme.colors.green[6]}
                >
                  Template
                </Text>
              </Group>
              <Button onClick={handleAutoImport} disabled={!autoImportFile}>
                Enviar
              </Button>
            </Group>
          </Tabs.Panel>
        </Tabs>
      </Modal>
    </>
  );
}